import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, MapPin, ArrowLeft, Loader2, AlertTriangle, Hotel, ExternalLink, Bookmark, Bed } from 'lucide-react';
import { LazyImage } from '@/components/ui/lazy-image';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { toast } from 'sonner';
import { ItineraryStructuredData } from '@/components/structured-data/ItineraryStructuredData';
import { BreadcrumbStructuredData, BreadcrumbItemType } from '@/components/structured-data/BreadcrumbStructuredData';
import { Helmet } from 'react-helmet';
import { Breadcrumb, BreadcrumbItem as UIBreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";

export default function PublicItineraryDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const baseUrl = window.location.origin;

  const { data: itinerary, isLoading, error, refetch } = useQuery({
    queryKey: ['public-itinerary', slug],
    queryFn: async () => {
      try {
        console.log(`Fetching itinerary data for: ${slug}`);
        
        try {
          console.log(`Calling get-itinerary edge function for: ${slug}`);
          
          if (!slug) {
            throw new Error('No slug or ID provided');
          }
          
          const { data, error: functionError } = await supabase.functions.invoke('get-itinerary', {
            body: { id: slug }
          });
          
          if (functionError) {
            console.error("Edge function error:", functionError);
            throw functionError;
          }
          
          if (data.error) {
            console.error("Edge function returned error:", data.error);
            throw new Error(data.error);
          }
          
          if (!data.itinerary) {
            console.error("No itinerary found with identifier:", slug);
            throw new Error('Itinerary not found');
          }
          
          console.log(`Successfully fetched itinerary from edge function: ${data.itinerary.title}`);
          return data.itinerary;
        } catch (edgeFunctionError) {
          console.warn(`Edge function failed, falling back to direct database queries:`, edgeFunctionError);
        }
        
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug || '');
        
        const query = supabase
          .from('itineraries')
          .select('*')
          .eq('status', 'published');
          
        const { data: itineraryData, error: itineraryError } = await (
          isUuid 
            ? query.eq('id', slug).maybeSingle()
            : query.eq('slug', slug).maybeSingle()
        );

        if (itineraryError) {
          console.error("Error fetching itinerary:", itineraryError);
          throw handleSupabaseError(itineraryError);
        }
        
        if (!itineraryData) {
          console.error("No itinerary found with identifier:", slug);
          throw new Error('Itinerary not found');
        }

        console.log(`Successfully fetched itinerary: ${itineraryData.title}`);

        const { data: daysData, error: daysError } = await supabase
          .from('itinerary_days')
          .select('*')
          .eq('itinerary_id', itineraryData.id)
          .order('day_number', { ascending: true });

        if (daysError) {
          console.warn("Error fetching itinerary days:", daysError);
        }
        const days = daysError ? [] : daysData;

        const { data: tagData, error: tagError } = await supabase
          .from('itinerary_tags')
          .select('tags:tag_id(id, name)')
          .eq('itinerary_id', itineraryData.id);
          
        if (tagError) {
          console.warn("Error fetching itinerary tags:", tagError);
        }
        
        const tags = tagData 
          ? tagData.map(item => (item.tags as unknown as { id: string; name: string })) 
          : [];

        return {
          ...itineraryData,
          itinerary_days: days,
          tags: tags
        };
      } catch (error) {
        console.error("Error in queryFn:", error);
        throw error;
      }
    },
    retry: 2,
    retryDelay: attempt => Math.min(attempt > 1 ? 2000 * Math.pow(2, attempt) : 1000, 30000),
    staleTime: 5 * 60 * 1000,
  });

  const handleManualRetry = () => {
    setRetryCount(prev => prev + 1);
    toast.info("Retrying to load itinerary...");
    refetch();
  };

  const breadcrumbItems: BreadcrumbItemType[] = [
    { name: 'Home', url: '/', position: 1 },
    { name: 'Itineraries', url: '/itineraries', position: 2 },
  ];
  
  if (itinerary) {
    breadcrumbItems.push({
      name: itinerary.title,
      url: `/itineraries/${itinerary.slug || itinerary.id}`,
      position: 3
    });
  }

  if (isLoading) {
    return (
      <div className="container py-10">
        <div className="flex flex-col justify-center items-center py-20 space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading itinerary details...</p>
        </div>
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="container py-10">
        <Card className="mx-auto max-w-3xl">
          <CardContent className="flex flex-col items-center justify-center py-10 space-y-6">
            <div className="flex items-center text-destructive">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <p className="text-lg font-medium">
                {error instanceof Error ? error.message : 'Itinerary not found'}
              </p>
            </div>
            
            <p className="text-muted-foreground text-center max-w-md">
              We're having trouble loading this itinerary. This could be due to network issues or the itinerary may no longer be available.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {retryCount < 3 && (
                <Button onClick={handleManualRetry}>
                  Try Again
                </Button>
              )}
              
              <Button variant="outline" asChild>
                <Link to="/itineraries">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Itineraries
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <ItineraryStructuredData itinerary={itinerary} baseUrl={baseUrl} />
      <BreadcrumbStructuredData items={breadcrumbItems} baseUrl={baseUrl} />
      
      <div className="container py-8 md:py-12">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <UIBreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </UIBreadcrumbItem>
            <BreadcrumbSeparator />
            <UIBreadcrumbItem>
              <BreadcrumbLink href="/itineraries">Itineraries</BreadcrumbLink>
            </UIBreadcrumbItem>
            <BreadcrumbSeparator />
            <UIBreadcrumbItem>
              <BreadcrumbPage>{itinerary.title}</BreadcrumbPage>
            </UIBreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-6">
          <Button variant="outline" asChild>
            <Link to="/itineraries">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to all itineraries
            </Link>
          </Button>
        </div>

        <Card className="overflow-hidden mb-8">
          {itinerary.featured_image && (
            <div className="w-full h-64 md:h-96 overflow-hidden">
              <LazyImage 
                src={itinerary.featured_image} 
                alt={itinerary.title} 
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-3xl">{itinerary.title}</CardTitle>
            {itinerary.description && (
              <CardDescription className="text-lg mt-2">
                <RichTextViewer content={itinerary.description} />
              </CardDescription>
            )}
            <div className="flex flex-wrap gap-4 text-muted-foreground text-sm mt-4">
              {itinerary.destination && (
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{itinerary.destination}</span>
                </div>
              )}
              {itinerary.duration && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{itinerary.duration} {itinerary.duration === 1 ? 'day' : 'days'}</span>
                </div>
              )}
              {itinerary.best_season && (
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Best season: {itinerary.best_season}</span>
                </div>
              )}
            </div>
            
            {itinerary.tags && itinerary.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {itinerary.tags.map(tag => (
                  <Badge key={tag.id} variant="secondary">{tag.name}</Badge>
                ))}
              </div>
            )}
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Day-by-Day Itinerary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {itinerary.itinerary_days && itinerary.itinerary_days.length > 0 ? (
              itinerary.itinerary_days.map((day, index) => (
                <div key={day.id} className="space-y-2">
                  {index > 0 && <Separator className="my-4" />}
                  <div className="flex items-center gap-2">
                    <Badge className="w-12 flex justify-center">Day {day.day_number}</Badge>
                    <h3 className="text-xl font-semibold">{day.title}</h3>
                  </div>
                  {day.location && (
                    <div className="flex items-center text-muted-foreground text-sm">
                      <MapPin className="h-4 w-4 mr-1" /> {day.location}
                    </div>
                  )}
                  {day.description && (
                    <div className="mt-2">
                      <RichTextViewer content={day.description} />
                    </div>
                  )}
                  
                  {day.accommodation && (
                    <div className="mt-4 p-4 bg-muted/30 rounded-md border">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Hotel className="h-4 w-4 text-primary" />
                          <h4 className="font-medium">Accommodation</h4>
                        </div>
                        
                        {day.accommodation.booking_link && (
                          <Button 
                            asChild
                            variant="outline" 
                            size="sm"
                            className="hover:bg-primary/10 hover:scale-105 transition-all duration-200 border-primary/50 shadow-sm"
                          >
                            <a 
                              href={day.accommodation.booking_link} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                            >
                              <Bed className="h-4 w-4 mr-1 text-primary" />
                              <span className="relative">
                                Reserve Room
                                <span className="absolute -top-2 -right-3 bg-red-500 text-white text-[10px] px-1 rounded-full">
                                  Book
                                </span>
                              </span>
                            </a>
                          </Button>
                        )}
                      </div>
                      
                      {day.accommodation.name && (
                        <div className="mb-2">
                          <p className="font-medium">{day.accommodation.name}</p>
                        </div>
                      )}
                      
                      {day.accommodation.notes && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          <RichTextViewer content={day.accommodation.notes} />
                        </div>
                      )}
                      
                      {day.accommodation.booking_link && (
                        <div className="mt-3">
                          <Button 
                            asChild
                            variant="default" 
                            size="sm"
                            className="hover:scale-105 transition-transform duration-200 bg-gradient-to-r from-primary to-primary/80 shadow-md relative group"
                          >
                            <a 
                              href={day.accommodation.booking_link} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="flex items-center"
                            >
                              <span className="mr-1">Book this accommodation</span>
                              <ExternalLink className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                              <span className="absolute -top-2 -right-2 bg-amber-400 text-xs font-bold text-black px-1.5 py-0.5 rounded-full animate-pulse">
                                Best Rate
                              </span>
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No day-by-day details available for this itinerary.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
